import { type InputHTMLAttributes, type Ref, useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxValue,
} from '@/components/Form/pickers/primitives/combobox';
import type { PickerSize } from '@/components/Form/pickers/primitives/pickerPrimitiveStyles';
import useControlled from '@/hooks/useControlled';
import { cn } from '@/lib/utils';

type ValidationStatus = 'error' | 'warning';

export type TokenizedTextAreaProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'defaultValue' | 'value' | 'onChange' | 'disabled' | 'size'
> & {
  className?: string;
  ref?: Ref<HTMLInputElement>;
  defaultValue?: string[];
  value?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  status?: ValidationStatus;
  size?: PickerSize;
  tokenSeparators?: string[];
  getChipClassName?: (value: string) => string | undefined;
};

const TOKEN_SEPARATORS = [',', ' ', '\n'];

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const trimAndFilter = (values: string[]) => values.map((value) => value.trim()).filter(Boolean);

const dedupe = (values: string[]) => Array.from(new Set(values));

const TokenizedTextArea = ({
  ref,
  className,
  value: valueFromProps,
  defaultValue,
  onChange: onChangeFromProps,
  disabled = false,
  status,
  size = 'large',
  tokenSeparators = TOKEN_SEPARATORS,
  getChipClassName,
  onKeyDown,
  onBlur,
  onPaste,
  ...props
}: TokenizedTextAreaProps) => {
  const [value = [], onValueChange] = useControlled(valueFromProps, defaultValue, (nextValues) => {
    onChangeFromProps?.(nextValues ?? []);
  });
  const [inputValue, setInputValue] = useState('');
  const resolvedStatus = status || (props['aria-invalid'] === true ? 'error' : undefined);
  const defaultChipClassName = cn(
    resolvedStatus === 'error' &&
      'bg-red-200 text-red-900 focus-visible:bg-red-300 focus-visible:outline-red-500',
    resolvedStatus === 'warning' &&
      'bg-yellow-200 text-yellow-900 focus-visible:bg-yellow-300 focus-visible:outline-yellow-500',
  );

  const separatorsRegex = useMemo(() => {
    if (tokenSeparators.length === 0) {
      return null;
    }

    return new RegExp(tokenSeparators.map(escapeRegExp).join('|'), 'g');
  }, [tokenSeparators]);

  const containsSeparator = (text: string) =>
    tokenSeparators.some((separator) => text.includes(separator));

  const addChips = (tokens: string[]) => {
    const nextValues = dedupe([...value, ...trimAndFilter(tokens)]);
    if (nextValues.length !== value.length) {
      onValueChange(nextValues);
    }
  };

  const commitInput = () => {
    const nextValue = inputValue.trim();
    if (nextValue.length === 0) {
      return;
    }

    addChips([nextValue]);
    setInputValue('');
  };

  const splitWithRemainder = (text: string) => {
    if (!separatorsRegex) {
      return { chips: [], remainder: text };
    }

    const segments = text.split(separatorsRegex);
    const hasTrailingSeparator = tokenSeparators.some((separator) => text.endsWith(separator));
    const chips = hasTrailingSeparator ? segments : segments.slice(0, -1);
    const remainder = hasTrailingSeparator ? '' : (segments.at(-1) ?? '');
    return { chips, remainder };
  };

  return (
    <Combobox
      autoHighlight={false}
      disabled={disabled}
      size={size}
      items={value}
      value={value}
      itemToStringLabel={(item) => item}
      itemToStringValue={(item) => item}
      aria-invalid={resolvedStatus === 'error' ? true : undefined}
      data-warning={resolvedStatus === 'warning' ? '' : undefined}
      multiple
      onValueChange={onValueChange}
    >
      <ComboboxChips className={className}>
        <ComboboxValue>
          {value.map((item) => {
            return (
              <ComboboxChip
                key={item}
                className={cn(defaultChipClassName, getChipClassName?.(item))}
              >
                {item}
              </ComboboxChip>
            );
          })}
        </ComboboxValue>
        <ComboboxChipsInput
          {...props}
          ref={ref}
          value={inputValue}
          onChange={(event) => {
            const nextInputValue = event.target.value;

            if (!separatorsRegex || !containsSeparator(nextInputValue)) {
              setInputValue(nextInputValue);
              return;
            }

            const { chips, remainder } = splitWithRemainder(nextInputValue);
            addChips(chips);
            setInputValue(remainder);
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);

            if (event.defaultPrevented) {
              return;
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              commitInput();
              return;
            }

            if (tokenSeparators.includes(event.key)) {
              event.preventDefault();
              commitInput();
            }
          }}
          onBlur={(event) => {
            commitInput();
            onBlur?.(event);
          }}
          onPaste={(event) => {
            const pastedValue = event.clipboardData.getData('text');
            if (!separatorsRegex || !containsSeparator(pastedValue)) {
              onPaste?.(event);
              return;
            }

            event.preventDefault();
            const { chips, remainder } = splitWithRemainder(`${inputValue}${pastedValue}`);
            addChips(chips);
            setInputValue(remainder);
            onPaste?.(event);
          }}
        />
      </ComboboxChips>
    </Combobox>
  );
};

export default TokenizedTextArea;
